import React, { Suspense, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './components/AuthContext';
import SidebarWrapper from './components/SidebarWrapper';
import { GlobalErrorBoundary } from './components/GlobalErrorBoundary';
import NotFoundPage from './components/NotFoundPage';
import CrmLogin from './components/CrmLogin';
import { API_BASE } from './lib/rpc';
import { ToastProvider } from './components/ui/ToastProvider';
import { DialogProvider } from './components/ui/DialogProvider';

// Dynamically import all page.tsx files exactly identically to Next.js App Router
const modules = import.meta.glob('./app/**/page.tsx');

function lazyWithRetry(componentImport: () => Promise<any>) {
  return React.lazy(async () => {
    const pageHasAlreadyBeenForceRefreshed = JSON.parse(
      window.sessionStorage.getItem('page-has-been-force-refreshed') || 'false'
    );
    try {
      const component = await componentImport();
      window.sessionStorage.setItem('page-has-been-force-refreshed', 'false');
      return component;
    } catch (error) {
      if (!pageHasAlreadyBeenForceRefreshed) {
        window.sessionStorage.setItem('page-has-been-force-refreshed', 'true');
        return window.location.reload();
      }
      throw error;
    }
  });
}

// Parse Next.js App Router paths into React Router DOM paths
const routes = Object.keys(modules).map((path) => {
  let routePath = path
    .replace('./app', '')
    .replace('/page.tsx', '')
    .replace(/\[([^\]]+)\]/g, ':$1') // [slug] -> :slug
    || '/';

  if (routePath === '') routePath = '/';

  return {
    path: routePath,
    component: lazyWithRetry(modules[path] as any)
  };
});

// Explicit mappings for special pages (e.g. nested auth paths or indexes)
const sortedRoutes = routes.sort((a, b) => b.path.length - a.path.length);

export default function App() {
  const location = useLocation();
  const isCrmSubdomain = typeof window !== 'undefined' && window.location.hostname === 'crm.dark.tel';

  useEffect(() => {
    if (isCrmSubdomain || location.pathname.startsWith('/services/crm/')) return;
    let link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    if (!link) {
       link = document.createElement('link');
       link.rel = 'manifest';
       document.head.appendChild(link);
    }
    const fetchBase = import.meta.env.DEV ? '' : API_BASE.replace(/\/$/, '');
    link.crossOrigin = "anonymous";
    link.href = `${fetchBase}/api/manifest.json?start_url=${encodeURIComponent(location.pathname + location.search)}&origin=${encodeURIComponent(window.location.origin)}&v=${Date.now()}`;
  }, [location, isCrmSubdomain]);

  if (isCrmSubdomain) {
     const CrmDashboard = lazyWithRetry(modules['./app/services/crm/[slug]/page.tsx'] as any);
     return (
         <GlobalErrorBoundary>
            <AuthProvider>
               <ToastProvider>
                  <DialogProvider>
                     <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Loading Interface...</div>}>
                        <Routes>
                           <Route path="/" element={<CrmLogin />} />
                           <Route path="/login" element={<CrmLogin />} />
                           <Route path="/:slug" element={<CrmDashboard />} />
                           <Route path="/services/crm/:slug" element={<CrmDashboard />} />
                           <Route path="*" element={<CrmLogin />} />
                        </Routes>
                     </Suspense>
                  </DialogProvider>
               </ToastProvider>
            </AuthProvider>
         </GlobalErrorBoundary>
     );
  }

  return (
    <GlobalErrorBoundary>
      <AuthProvider>
        <ToastProvider>
          <DialogProvider>
            <SidebarWrapper>
              <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Loading Interface...</div>}>
                <Routes>
                  {sortedRoutes.map(({ path, component: Component }) => (
                    <Route key={path} path={path} element={<Component />} />
                  ))}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </SidebarWrapper>
          </DialogProvider>
        </ToastProvider>
      </AuthProvider>
    </GlobalErrorBoundary>
  );
}
